import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../../../app/api/api';
import KitchenSubOrder from '../../../../app/data/models/orders/kitchenSubOrder';
import OrderItem from '../../../../app/data/models/orders/orderItem';
import { OrderStatus } from '../../../../app/data/models/orders/orderStatus';
import {
  OrderNotification,
  OrderStatusUpdate,
} from '../../../../app/data/models/orders/orderItemUpdateStatus';
import mapKitchenSubOrderToSubOrderStatusUpdate from '../../../../app/data/mappers/mapKitchenSubOrderToSubOrderStatusUpdate';
import {
  createEventSourceCloseThunk,
  createEventSourceThunk,
  getEventSourceConnectionStatus,
  reconnectEventSource,
} from '../../../../app/managers/eventSourceManager';
import { CurrentPageDateItemDto } from '../../../../app/data/models/common/currentPageDateItemDto';
import { AppDispatch } from '../../../../app/store/configureStore';

// Stream connection status
export enum StreamConnectionStatus {
  NotConnected = 'not_connected',
  Connected = 'connected',
  Disconnected = 'disconnected',
}

export interface KitchenOrdersState {
  pendingKitchenOrders: KitchenSubOrder[];
  completedKitchenOrders: KitchenSubOrder[];
  // Pagination dates for pending orders
  pendingNewestDate: string | null;
  pendingOldestDate: string | null;
  // Pagination dates for completed orders
  completedNewestDate: string | null;
  completedOldestDate: string | null;
  // Loading states
  isPendingLoading: boolean;
  isCompletedLoading: boolean;
  isPendingRefreshing: boolean;
  isCompletedRefreshing: boolean;
  // Update queues
  pendingUpdatesQueue: { [key: string]: KitchenSubOrder };
  completedUpdatesQueue: { [key: string]: KitchenSubOrder };
  pendingError: string | null;
  completedError: string | null;
  hasCompletedInitialLoad: boolean;
  // Connection status
  connectionStatus: StreamConnectionStatus;
}

const initialState: KitchenOrdersState = {
  pendingKitchenOrders: [],
  completedKitchenOrders: [],
  // Pagination dates for pending orders
  pendingNewestDate: null,
  pendingOldestDate: null,
  // Pagination dates for completed orders
  completedNewestDate: null,
  completedOldestDate: null,
  // Loading states
  isPendingLoading: true,
  isCompletedLoading: false,
  isPendingRefreshing: false,
  isCompletedRefreshing: false,
  // Update queues
  pendingUpdatesQueue: {},
  completedUpdatesQueue: {},
  pendingError: null,
  completedError: null,
  hasCompletedInitialLoad: false,
  // Connection status
  connectionStatus: StreamConnectionStatus.NotConnected,
};

const splitOrdersByStatus = (orders: KitchenSubOrder[]) => {
  const pending = orders.filter(
    (order) =>
      order.orderStatus === OrderStatus.Pending ||
      order.orderStatus === OrderStatus.Processing
  );
  const completed = orders.filter(
    (order) =>
      order.orderStatus === OrderStatus.Ready ||
      order.orderStatus === OrderStatus.Cancelled
  );

  return { pending, completed };
};

export interface KitchenOrdersParams {
  restaurantId: string;
  kitchenId: string;
  nextPageId?: string;
  previousPageId?: string;
  pageSize?: number;
  statuses?: OrderStatus[];
}

export const fetchKitchenOrdersAsync = createAsyncThunk<
  CurrentPageDateItemDto<KitchenSubOrder>,
  {
    restaurantId: string;
    kitchenId: string;
    nextPageDate?: string;
    previousPageDate?: string;
    pageSize?: number;
    statuses?: OrderStatus[];
  }
>(
  'kitchenOrders/fetchOrders',
  async ({
    restaurantId,
    kitchenId,
    nextPageDate,
    previousPageDate,
    pageSize,
    statuses,
  }) => {
    const response = await api.Kitchen.orders(
      restaurantId,
      kitchenId,
      nextPageDate,
      previousPageDate,
      pageSize,
      statuses
    );
    return response;
  }
);

export const updateKitchenOrderItemUpdateAsync = createAsyncThunk<
  void,
  {
    orderItemId: string;
    kitchenOrderId: string;
    restaurantId: string;
    orderId: string;
    kitchenId: string;
    kitchenOrders: KitchenSubOrder[];
  }
>(
  'kitchens/orders/updateOrderItemStatus',
  async (
    {
      orderItemId,
      kitchenOrderId,
      restaurantId,
      orderId,
      kitchenId,
      kitchenOrders,
    },
    thunkApi
  ) => {
    try {
      if (!kitchenOrders) return;
      const updatedSubOrders: KitchenSubOrder[] = [];
      const modifiedKitchenOrders = kitchenOrders.map((subOrder) => {
        if (subOrder.id !== kitchenOrderId) return subOrder;
        const orderItems = subOrder.orderItems.map((orderItem: OrderItem) =>
          orderItem.id === orderItemId
            ? { ...orderItem, isCompleted: !orderItem.isCompleted }
            : orderItem
        );
        const isReady = orderItems.every((item) => item.isCompleted);
        const orderStatus: OrderStatus = isReady
          ? OrderStatus.Ready
          : OrderStatus.Processing;
        const updatedSubOrder = {
          ...subOrder,
          orderItems,
          orderStatus,
        };
        if (subOrder.orderStatus != orderStatus) {
          updatedSubOrders.push(updatedSubOrder);
        }
        return updatedSubOrder;
      });
      thunkApi.dispatch(setKitchenOrders(modifiedKitchenOrders));
      if (updatedSubOrders.length !== 0) {
        thunkApi.dispatch(
          updateKitchenOrderStatusAsync({
            restaurantId,
            orderId,
            kitchenId,
            orderStatusUpdate: {
              orderId,
              subOrderStatuses: updatedSubOrders.map(
                mapKitchenSubOrderToSubOrderStatusUpdate
              ),
            },
          })
        );
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        return thunkApi.rejectWithValue({ error: error.message });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const updateKitchenOrderStatusAsync = createAsyncThunk<
  void,
  {
    restaurantId: string;
    orderId: string;
    kitchenId: string;
    orderStatusUpdate: OrderStatusUpdate;
  }
>(
  'kitchens/orders/updateStatus',
  async ({ restaurantId, orderId, kitchenId, orderStatusUpdate }, thunkApi) => {
    try {
      // Create a new ISO string for the current time to ensure consistency.
      const statusChangedAt = new Date().toISOString();

      // Dispatch status update immediately for an optimistic UI update.
      thunkApi.dispatch(
        updateKitchenOrderStatus({
          updatedOrderStatuses: [
            {
              orderId: orderId,
              subOrderStatuses: orderStatusUpdate.subOrderStatuses.map(
                (status) => ({
                  ...status,
                  orderItemStatuses: status.orderItemStatuses || [],
                  // Optimistically set the statusChangedAt time.
                  statusChangedAt: statusChangedAt,
                })
              ),
            },
          ],
        })
      );

      await api.Kitchen.updateOrderStatus(
        restaurantId,
        orderId,
        kitchenId,
        orderStatusUpdate
      );
    } catch (error: unknown) {
      if (error instanceof Error) {
        return thunkApi.rejectWithValue({ error: error.message });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const observeOrderStatusChange = () =>
  createEventSourceThunk(
    'kitchens/orders/observerOrderStatusChange',
    (arg: unknown) => {
      const { restaurantId, kitchenId } = arg as {
        restaurantId: string;
        kitchenId: string;
      };
      return `manager/${restaurantId}/kitchen/${kitchenId}/order-notification`;
    },
    (dispatch: AppDispatch, event: MessageEvent) => {
      // When data is received, update our store with the new data
      const data = JSON.parse(event.data);
      dispatch(updateKitchenOrderStatus(data));
    },
    (dispatch: AppDispatch, isConnected: boolean) => {
      // Update connection status when it changes
      dispatch(
        setConnectionStatus(
          isConnected
            ? StreamConnectionStatus.Connected
            : StreamConnectionStatus.Disconnected
        )
      );
    }
  );

export const closeOrderStatusObserver = createEventSourceCloseThunk(
  'kitchens/orders/observerOrderStatusChange'
);

export const manualReconnectOrderStatusObserver = () => {
  return (arg: { restaurantId: string; kitchenId: string }) =>
    (dispatch: AppDispatch) => {
      // First, close any existing connection
      dispatch(closeOrderStatusObserver());

      // Then try to reconnect
      const isReconnected = reconnectEventSource(
        'kitchens/orders/observerOrderStatusChange'
      );

      if (!isReconnected) {
        // If reconnection failed through the manager, re-establish the connection completely
        dispatch(observeOrderStatusChange()(arg));
      }

      // Update the connection status in the UI after a short delay to allow connection to establish
      setTimeout(() => {
        const currentStatus = getEventSourceConnectionStatus(
          'kitchens/orders/observerOrderStatusChange'
        );
        dispatch(
          setConnectionStatus(
            currentStatus
              ? StreamConnectionStatus.Connected
              : StreamConnectionStatus.Disconnected
          )
        );
      }, 1000);
    };
};

export const getStreamConnectionStatus = () => {
  return getEventSourceConnectionStatus(
    'kitchens/orders/observerOrderStatusChange'
  )
    ? StreamConnectionStatus.Connected
    : StreamConnectionStatus.Disconnected;
};

const updateOrderWithStatus = (
  orderNotification: OrderNotification,
  pendingOrders: KitchenSubOrder[],
  completedOrders: KitchenSubOrder[]
) => {
  const subOrderStatuses =
    orderNotification.updatedOrderStatuses?.flatMap(
      (o) => o.subOrderStatuses
    ) ?? [];
  const createdOrders = orderNotification.createdOrders ?? [];

  // Remove orders that are being updated from both arrays first
  const orderIdsToUpdate = new Set(
    subOrderStatuses.map((status) => status.subOrderId)
  );
  const filteredPendingOrders = pendingOrders.filter(
    (order) => !orderIdsToUpdate.has(order.id)
  );
  const filteredCompletedOrders = completedOrders.filter(
    (order) => !orderIdsToUpdate.has(order.id)
  );

  // Process all orders (existing + created)
  const allOrders = [...pendingOrders, ...completedOrders]
    .filter((order) => orderIdsToUpdate.has(order.id))
    .map((order) => {
      const subOrderStatus = subOrderStatuses.find(
        (subOrder) => subOrder.subOrderId === order.id
      );
      if (!subOrderStatus) return order;

      const orderItems = order.orderItems.map((orderItem) => {
        const subOrderStatusItem = subOrderStatus?.orderItemStatuses?.find(
          (statuses) => statuses.orderItemId === orderItem.id
        );
        if (!subOrderStatusItem) return orderItem;
        return {
          ...orderItem,
          isCompleted: subOrderStatusItem.isCompleted,
        };
      });

      return {
        ...order,
        orderItems,
        orderStatus: subOrderStatus.orderStatus ?? order.orderStatus,
        statusChangedAt:
          subOrderStatus.statusChangedAt ?? order.statusChangedAt,
      };
    });

  // Add new orders
  allOrders.push(...createdOrders);

  // Split updated/new orders into pending and completed
  const { pending, completed } = splitOrdersByStatus(allOrders);

  // Sort orders by statusChangedAt or orderDate in descending order (newest first)
  const sortByDate = (a: KitchenSubOrder, b: KitchenSubOrder) => {
    const aTime = a.statusChangedAt
      ? new Date(a.statusChangedAt).getTime()
      : new Date(a.orderDate).getTime();
    const bTime = b.statusChangedAt
      ? new Date(b.statusChangedAt).getTime()
      : new Date(b.orderDate).getTime();
    return bTime - aTime;
  };

  return {
    pendingOrders: [...filteredPendingOrders, ...pending].sort(sortByDate),
    completedOrders: [...filteredCompletedOrders, ...completed].sort(
      sortByDate
    ),
  };
};

// Helper function to merge orders without duplicates and sort by statusChangedAt or orderDate
const mergeOrders = (
  existing: KitchenSubOrder[],
  incoming: KitchenSubOrder[]
): KitchenSubOrder[] => {
  const merged = [...existing];
  incoming.forEach((order) => {
    const existingIndex = merged.findIndex((e) => e.id === order.id);
    if (existingIndex === -1) {
      merged.push(order);
    } else {
      merged[existingIndex] = order; // Update existing order with new data
    }
  });

  // Sort by statusChangedAt or orderDate in descending order (newest first)
  return merged.sort((a, b) => {
    const aTime = a.statusChangedAt
      ? new Date(a.statusChangedAt).getTime()
      : new Date(a.orderDate).getTime();
    const bTime = b.statusChangedAt
      ? new Date(b.statusChangedAt).getTime()
      : new Date(b.orderDate).getTime();
    return bTime - aTime;
  });
};

export const kitchenOrdersSlice = createSlice({
  name: 'kitchenOrders',
  initialState,
  reducers: {
    setKitchenOrders: (state, action) => {
      const { pending, completed } = splitOrdersByStatus(action.payload);

      // Filter out existing orders from both arrays
      const existingPendingOrders = state.pendingKitchenOrders.filter(
        (order) =>
          !pending.some((p) => p.id === order.id) &&
          !completed.some((c) => c.id === order.id)
      );

      const existingCompletedOrders = state.completedKitchenOrders.filter(
        (order) =>
          !pending.some((p) => p.id === order.id) &&
          !completed.some((c) => c.id === order.id)
      );

      // Merge existing orders with new ones, ensuring no duplicates
      state.pendingKitchenOrders = [...existingPendingOrders, ...pending];

      state.completedKitchenOrders = [...existingCompletedOrders, ...completed];
    },
    updateKitchenOrderStatus: (state, action) => {
      const { pendingOrders, completedOrders } = updateOrderWithStatus(
        action.payload,
        state.pendingKitchenOrders,
        state.completedKitchenOrders
      );
      state.pendingKitchenOrders = pendingOrders;
      state.completedKitchenOrders = completedOrders;
    },
    setConnectionStatus: (state, action) => {
      state.connectionStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle reset action
    builder.addCase(resetKitchenOrders, (state) => {
      return {
        ...initialState,
        // Keep the connection status when resetting
        connectionStatus: state.connectionStatus,
      };
    });

    builder
      .addCase(fetchKitchenOrdersAsync.pending, (state, action) => {
        const isPendingTab = action.meta.arg.statuses?.some(
          (status) =>
            status === OrderStatus.Pending || status === OrderStatus.Processing
        );

        if (action.meta.arg.previousPageDate) {
          // Loading more previous orders
          if (isPendingTab) {
            state.isPendingLoading = true;
            state.pendingError = null; // Reset error
          } else {
            state.isCompletedLoading = true;
            state.completedError = null; // Reset error
          }
        } else if (action.meta.arg.nextPageDate) {
          // Refreshing to get newer orders
          if (isPendingTab) {
            state.isPendingRefreshing = true;
            state.pendingError = null; // Reset error
          } else {
            state.isCompletedRefreshing = true;
            state.completedError = null; // Reset error
          }
        } else {
          // Initial load
          if (isPendingTab) {
            state.isPendingLoading = true;
            state.pendingError = null; // Reset error
          } else {
            state.isCompletedLoading = true;
            state.completedError = null; // Reset error
          }
        }
      })
      .addCase(fetchKitchenOrdersAsync.fulfilled, (state, action) => {
        const { pending, completed } = splitOrdersByStatus(
          action.payload.data.map((order) => ({
            ...order,
            orderId: order.orderId, // Explicitly map orderId
          }))
        );

        const isPendingTab = action.meta.arg.statuses?.some(
          (status) =>
            status === OrderStatus.Pending || status === OrderStatus.Processing
        );

        if (action.meta.arg.previousPageDate) {
          // Loading more previous orders
          if (isPendingTab) {
            state.pendingKitchenOrders = mergeOrders(
              state.pendingKitchenOrders,
              pending
            );
          } else {
            state.completedKitchenOrders = mergeOrders(
              state.completedKitchenOrders,
              completed
            );
          }
        } else if (action.meta.arg.nextPageDate) {
          // Refreshing to get newer orders
          if (isPendingTab) {
            state.pendingKitchenOrders = mergeOrders(
              state.pendingKitchenOrders,
              pending
            );
          } else {
            state.completedKitchenOrders = mergeOrders(
              state.completedKitchenOrders,
              completed
            );
          }
        } else {
          // Initial load
          if (isPendingTab) {
            state.pendingKitchenOrders = pending;
          } else {
            state.completedKitchenOrders = completed;
          }
        }

        // Update pagination state for pending tab
        if (isPendingTab) {
          const newNextDate =
            typeof action.payload.nextPageDate === 'string'
              ? action.payload.nextPageDate
              : action.payload.nextPageDate?.toISOString() || null;

          const newPrevDate =
            typeof action.payload.previousPageDate === 'string'
              ? action.payload.previousPageDate
              : action.payload.previousPageDate?.toISOString() || null;

          if (action.meta.arg.previousPageDate) {
            // Loading previous page
            state.pendingOldestDate = newPrevDate;
            // Keep existing newestDate
            if (!state.pendingNewestDate && newNextDate) {
              state.pendingNewestDate = newNextDate;
            }
          } else if (action.meta.arg.nextPageDate) {
            // Refreshing newer items
            if (newNextDate) {
              state.pendingNewestDate = newNextDate;
            }
            // Keep existing oldestDate
            if (!state.pendingOldestDate && newPrevDate) {
              state.pendingOldestDate = newPrevDate;
            }
          } else {
            // Initial load
            if (newNextDate) {
              state.pendingNewestDate = newNextDate;
            }
            state.pendingOldestDate = newPrevDate;
          }
        } else {
          const newNextDate =
            typeof action.payload.nextPageDate === 'string'
              ? action.payload.nextPageDate
              : action.payload.nextPageDate?.toISOString() || null;

          const newPrevDate =
            typeof action.payload.previousPageDate === 'string'
              ? action.payload.previousPageDate
              : action.payload.previousPageDate?.toISOString() || null;

          if (action.meta.arg.previousPageDate) {
            // Loading previous page
            state.completedOldestDate = newPrevDate;
            // Keep existing newestDate
            if (!state.completedNewestDate && newNextDate) {
              state.completedNewestDate = newNextDate;
            }
          } else if (action.meta.arg.nextPageDate) {
            // Refreshing newer items
            if (newNextDate) {
              state.completedNewestDate = newNextDate;
            }
            // Keep existing oldestDate
            if (!state.completedOldestDate && newPrevDate) {
              state.completedOldestDate = newPrevDate;
            }
          } else {
            // Initial load
            if (newNextDate) {
              state.completedNewestDate = newNextDate;
            }
            state.completedOldestDate = newPrevDate;
          }
        }

        state.isPendingLoading = false;
        state.isCompletedLoading = false;
        state.isPendingRefreshing = false;
        state.isCompletedRefreshing = false;

        if (!isPendingTab) {
          state.hasCompletedInitialLoad = true;
        }
      })
      .addCase(fetchKitchenOrdersAsync.rejected, (state, action) => {
        const isPendingTab = action.meta.arg.statuses?.some(
          (status) =>
            status === OrderStatus.Pending || status === OrderStatus.Processing
        );

        if (isPendingTab) {
          state.isPendingLoading = false;
          state.isPendingRefreshing = false;
          state.pendingError = 'Failed to load orders';
        } else {
          state.isCompletedLoading = false;
          state.isCompletedRefreshing = false;
          state.completedError = 'Failed to load orders';
          state.hasCompletedInitialLoad = true; // Mark as attempted load even on failure
        }
      });
  },
});

// Action to reset the state when switching between kitchens
export const resetKitchenOrders = createAction('kitchenOrders/reset');

export const {
  updateKitchenOrderStatus,
  setKitchenOrders,
  setConnectionStatus,
} = kitchenOrdersSlice.actions;
